/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import * as appStrings from '@touchplan/strings';
import { ILoggerService } from '@touchplan/logging';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { OnStoreInit } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { combineLatest, EMPTY, filter, map, switchMap, take } from 'rxjs';
import { ActualPhase, Role, Ticket, TicketType } from '@touchplan/common';
import {
	fancyFirebaseGetters,
	fancyFirebaseReferencer,
	UserPreferenceCategories,
} from '@touchplan/fancy-referencer';

import { doesValueMatchCriteria } from 'src/app/utils/comparison-utils';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { CustomFieldDefinition } from 'src/app/app-data/models/CustomFieldDefinition';
import {
	ITPFilter,
	TextPredicateOp,
	DatePredicateOp,
	CommonPredicateOp,
	TpFilterPredicateOp,
	ICustomFieldFilter,
	NumericPredicateOp,
} from 'src/app/app-data/models/TPFilterPredicates';
import {
	FilterFormCriteria,
	FilterOperator,
	IPlanTicketsAttachments,
	ITicketCustomFields,
	TicketAttachmentOptions,
	TicketStatusOptions,
	TicketValue,
} from '../interfaces/plan-tickets-filter.interface';
import { ITicketAttachmentList } from 'src/app/app-data/models/Attachment';
import { Swimlane } from 'src/app/app-data/models/Swimlane';
import RBush from 'rbush';
import { TPStateManager } from 'src/app/services/tp-state-manager.service';

import firebase from 'firebase/compat/app';

interface IPlanTicketsFilterState {
	projectId: string;
	userId: string;
	ticketTypeOptions: Map<string, TicketType>;
	filterOperatorOptions: Map<string, string>;
	ticketStatusOptions: Map<string, string>;
	ticketAttachmentOptions: Map<string, string>;
	selectedFilterOperator: FilterOperator;
	matchingTickets: Map<string, Ticket>;
	filterFormCriteria: FilterFormCriteria | null;
	serverDataLoaded: boolean;
	ticketCustomFields: ITicketCustomFields;
	ticketAttachments: IPlanTicketsAttachments;
	quickSearch: ITPFilter;
	swimlanesTree: RBush | null;
}

// Special "Observables" state (being handled by TPStateManager)
interface IPlanTicketsFilterObsState {
	planTickets: Map<string, Ticket>;
	locationOptions: Map<string, Location>;
	roleOptions: Map<string, Role>;
	swimlaneOptions: Map<string, Swimlane>;
	customFieldOptions: Map<string, CustomFieldDefinition>;
}

interface IFirebaseCriteriaStore extends FilterFormCriteria {
	filterOperator: FilterOperator;
}

const TicketStatusMap = new Map<string, string>([
	[ActualPhase.actualized, TicketStatusOptions.Completed],
	[ActualPhase.actualizing, TicketStatusOptions.Completed],
	[ActualPhase.plannedPreview, TicketStatusOptions.Completed],
	[ActualPhase.started, TicketStatusOptions.Started],
	[ActualPhase.planned, TicketStatusOptions.Todo],
	// ActualPhase.multiple is not included as a valid ticket status
]);

// Define string literals for ticket fields
const TicketFields = {
	ActivityName: 'activityName',
	LiveStart: 'liveStart',
	LocationId: 'locationId',
	RoleId: 'roleId',
	ExternalScheduleId: 'externalScheduleId',
	Type: 'type',
	WbsCode: 'wbsCode',
	TicketStatus: 'actualPhase',
};

const FormFields = {
	Description: 'description',
	FromDate: 'fromDate',
	ToDate: 'toDate',
	LocationIds: 'locationIds',
	RoleIds: 'roleIds',
	ExternalScheduleId: 'externalScheduleId',
	Type: 'type',
	WbsCode: 'wbsCode',
	TicketAttachments: 'ticketAttachments',
	AttachmentComment: 'attachmentComment',
	TicketStatus: 'ticketStatus',
	CustomField1: 'customField1',
	CustomField2: 'customField2',
	CustomField3: 'customField3',
	SwimlaneIds: 'swimlaneIds',
};

const INITIAL_STATE: IPlanTicketsFilterState = {
	projectId: null,
	userId: null,
	ticketTypeOptions: new Map<string, TicketType>([
		[appStrings.TICKET_TYPE_TASK, TicketType.Task],
		[appStrings.TICKET_TYPE_MILESTONE, TicketType.Milestone],
		[appStrings.TICKET_TYPE_CONSTRAINT_UNASSIGNED, TicketType.Constraint],
	]),
	filterOperatorOptions: new Map<string, string>([
		[appStrings.FILTER_AND_OPERATOR, FilterOperator.AND],
		[appStrings.FILTER_OR_OPERATOR, FilterOperator.OR],
	]),
	ticketStatusOptions: new Map<string, string>([
		[appStrings.UICONTROL_TEXT_COMPLETED, TicketStatusOptions.Completed],
		[appStrings.UICONTROL_TEXT_STARTED, TicketStatusOptions.Started],
		[appStrings.UICONTROL_TEXT_TODO, TicketStatusOptions.Todo],
	]),
	ticketAttachmentOptions: new Map<string, string>([
		[appStrings.PLAN_FILTER_TICKET_ATTACHMENT_ALL_TICKET_OPTION, TicketAttachmentOptions.Any],
		[appStrings.PLAN_FILTER_TICKET_WITH_ATTACHMENTS_OPTION, TicketAttachmentOptions.WithAttachments],
		[appStrings.PLAN_FILTER_TICKET_WITHOUT_ATTACHMENTS_OPTION, TicketAttachmentOptions.WithoutAttachments],
	]),
	selectedFilterOperator: FilterOperator.AND,
	matchingTickets: new Map<string, Ticket>(),
	filterFormCriteria: null,
	serverDataLoaded: false,
	ticketCustomFields: null,
	ticketAttachments: null,
	quickSearch: null,
	swimlanesTree: null,
};

// Initial state for the external Observables that we will use
const INITIAL_OBS_STATE: IPlanTicketsFilterObsState = {
	planTickets: new Map<string, Ticket>(),
	locationOptions: new Map<string, Location>(),
	roleOptions: new Map<string, Role>(),
	swimlaneOptions: new Map<string, Swimlane>(),
	customFieldOptions: new Map<string, CustomFieldDefinition>(),
};

/**
 * Represents a map that defines the criteria for filtering plan tickets.
 * @property {string} key - The criteria key. Match this with the control name.
 * @property {string} ticketField - The corresponding ticket field to filter.
 * @property {TpFilterPredicateOp} [comparisonType] - The comparison type for the filter.
 */
const criteriaMap = new Map<
	string,
	{ ticketField: string | undefined; comparisonType: TpFilterPredicateOp | undefined }
>([
	[
		FormFields.Description,
		{ ticketField: TicketFields.ActivityName, comparisonType: TextPredicateOp.Contains },
	],
	[
		FormFields.FromDate,
		{ ticketField: TicketFields.LiveStart, comparisonType: DatePredicateOp.GreaterThanOrEqualTo },
	],
	[
		FormFields.ToDate,
		{ ticketField: TicketFields.LiveStart, comparisonType: DatePredicateOp.LessThanOrEqualTo },
	],
	[
		FormFields.LocationIds,
		{ ticketField: TicketFields.LocationId, comparisonType: CommonPredicateOp.EqualTo },
	],
	[FormFields.RoleIds, { ticketField: TicketFields.RoleId, comparisonType: CommonPredicateOp.EqualTo }],
	[
		FormFields.ExternalScheduleId,
		{ ticketField: TicketFields.ExternalScheduleId, comparisonType: TextPredicateOp.ExactlyMatches },
	],
	[FormFields.Type, { ticketField: TicketFields.Type, comparisonType: TextPredicateOp.ExactlyMatches }],
	[FormFields.WbsCode, { ticketField: TicketFields.WbsCode, comparisonType: undefined }],
	[
		FormFields.TicketStatus,
		{ ticketField: TicketFields.TicketStatus, comparisonType: CommonPredicateOp.EqualTo },
	],
	[FormFields.TicketAttachments, { ticketField: undefined, comparisonType: undefined }],
	[FormFields.CustomField1, { ticketField: undefined, comparisonType: undefined }],
	[FormFields.CustomField2, { ticketField: undefined, comparisonType: undefined }],
	[FormFields.CustomField3, { ticketField: undefined, comparisonType: undefined }],
	[FormFields.AttachmentComment, { ticketField: undefined, comparisonType: undefined }],
]);

@Injectable()
export class PlanTicketsFilterState
	extends TPStateManager<IPlanTicketsFilterState, IPlanTicketsFilterObsState>
	implements OnDestroy, OnStoreInit
{
	private fireDB = inject(AngularFireDatabase);
	private logger: ILoggerService = LoggerServiceFactory.create('PlanTicketsFilterStore');

	// Selectors for external Observables (see TPStateManager to know how these are getting handled)
	readonly planTickets$ = this.selectLive$<Map<string, Ticket>>('planTickets');
	readonly locationOptions$ = this.selectLive$<Map<string, Location>>('locationOptions');
	readonly roleOptions$ = this.selectLive$<Map<string, Role>>('roleOptions');
	readonly swimlaneOptions$ = this.selectLive$<Map<string, Swimlane>>('swimlaneOptions');
	readonly customFieldOptions$ = this.selectLive$<Map<string, CustomFieldDefinition>>('customFieldOptions');

	// Updaters for external Observables (see TPStateManager to know how these are getting handled)
	public readonly setPlanTickets = this.liveUpdater('planTickets');
	public readonly setLocationOptions = this.liveUpdater('locationOptions');
	public readonly setRoleOptions = this.liveUpdater('roleOptions');
	public readonly setSwimlaneOptions = this.liveUpdater('swimlaneOptions');
	public readonly setCustomFieldOptions = this.liveUpdater('customFieldOptions');

	// Selectors
	readonly ticketTypeOptions$ = this.select((state) => state.ticketTypeOptions);
	readonly filterOperatorOptions$ = this.select((state) => state.filterOperatorOptions);
	readonly filterFormCriteria$ = this.select((state) => state.filterFormCriteria);
	readonly ticketStatusOptions$ = this.select((state) => state.ticketStatusOptions);
	readonly selectedFilterOperator$ = this.select((state) => state.selectedFilterOperator);
	readonly matchingTickets$ = this.select((state) => state.matchingTickets);
	readonly projectId$ = this.select((state) => state.projectId);
	readonly userId$ = this.select((state) => state.userId);
	readonly serverDataLoaded$ = this.select((state) => state.serverDataLoaded);
	readonly ticketCustomFields$ = this.select((state) => state.ticketCustomFields);
	readonly ticketAttachments$ = this.select((state) => state.ticketAttachments);
	readonly ticketAttachmentOptions$ = this.select((state) => state.ticketAttachmentOptions);
	readonly quickSearch$ = this.select((state) => state.quickSearch);
	readonly swimlanesTree$ = this.select((state) => state.swimlanesTree);

	// Updaters
	public readonly setTicketTypeOptions = this.updater(
		(state: IPlanTicketsFilterState, ticketTypeOptions: Map<string, TicketType>) => ({
			...state,
			ticketTypeOptions,
		})
	);
	public readonly setFilterOperatorOptions = this.updater(
		(state: IPlanTicketsFilterState, filterOperatorOptions: Map<string, string>) => ({
			...state,
			filterOperatorOptions,
		})
	);
	public readonly setSelectedFilterOperator = this.updater(
		(state: IPlanTicketsFilterState, selectedFilterOperator: FilterOperator) => ({
			...state,
			selectedFilterOperator,
		})
	);
	public readonly setMatchingTickets = this.updater(
		(state: IPlanTicketsFilterState, matchingTickets: Map<string, Ticket>) => ({ ...state, matchingTickets })
	);
	public readonly setFilterFormCriteria = this.updater(
		(state: IPlanTicketsFilterState, filterFormCriteria: { [controlName: string]: any }) => ({
			...state,
			filterFormCriteria,
		})
	);
	public readonly setQuickSearch = this.updater((state: IPlanTicketsFilterState, quickSearch: ITPFilter) => ({
		...state,
		quickSearch,
	}));
	public readonly setProjectId = this.updater((state: IPlanTicketsFilterState, projectId: string) => ({
		...state,
		projectId,
	}));
	public readonly setUserId = this.updater((state: IPlanTicketsFilterState, userId: string) => ({
		...state,
		userId,
	}));
	public readonly setTicketCustomFields = this.updater(
		(state: IPlanTicketsFilterState, ticketCustomFields: ITicketCustomFields) => ({
			...state,
			ticketCustomFields,
		})
	);
	private readonly setServerDataLoaded = this.updater(
		(state: IPlanTicketsFilterState, serverDataLoaded: boolean) => ({
			...state,
			serverDataLoaded,
		})
	);
	private readonly setSwimlanesTree = this.updater(
		(state: IPlanTicketsFilterState, swimlanesTree: RBush) => ({
			...state,
			swimlanesTree,
		})
	);
	public readonly setTicketAttachments = this.updater(
		(state: IPlanTicketsFilterState, ticketAttachments: ITicketAttachmentList) => ({
			...state,
			ticketAttachments: this.processTicketAttachmentsForFilter(ticketAttachments),
		})
	);

	// effects
	public filterTickets = this.effect<FilterFormCriteria>((filterFormCriteria$) => {
		return combineLatest([
			filterFormCriteria$,
			this.planTickets$,
			this.selectedFilterOperator$,
			this.quickSearch$,
		]).pipe(
			map(([filterFormCriteria, tickets, filterOperator, quickSearch]) => ({
				filteredTickets: this.getFilteredTickets(tickets, filterFormCriteria, filterOperator),
				filterFormCriteria,
				filterOperator,
				isQuickSearchMode: !!(quickSearch && quickSearch.value),
			})),
			tapResponse<{
				filteredTickets: Map<string, Ticket>;
				filterFormCriteria: FilterFormCriteria;
				filterOperator: FilterOperator;
				isQuickSearchMode: boolean;
			}>(
				({ filteredTickets, filterFormCriteria, filterOperator, isQuickSearchMode }) => {
					this.setMatchingTickets(filteredTickets);
					if (!isQuickSearchMode) {
						this.storeFilterCriteriaValues({ ...filterFormCriteria, filterOperator });
					}
				},
				(error) => {
					this.logger.error(error);
				}
			)
		);
	});

	public resetFilteringProperties = this.effect((obs$) => {
		return obs$.pipe(
			tapResponse(
				() => {
					this.setMatchingTickets(new Map<string, Ticket>());
				},
				(err) => {
					this.logger.error('Error resetting filtering properties ', err);
				}
			)
		);
	});

	// Filter Methods
	/**
	 * Retrieves the filtered tickets based on the provided filter criteria and operator.
	 *
	 * @param tickets - The map of tickets to filter.
	 * @param filterFormCriteria - The criteria used for filtering the tickets.
	 * @param filterOperator - The operator used for combining the filter criteria AND / OR.
	 * @returns An object containing the matching tickets and non-matching tickets.
	 * Note: This method should be tested with a lot of tickets to ensure the performance.
	 * Some optimizations can be made if needed.
	 */
	private getFilteredTickets(
		tickets: Map<string, Ticket>,
		filterFormCriteria: FilterFormCriteria,
		filterOperator: FilterOperator
	): Map<string, Ticket> {
		const matchingTickets = new Map<string, Ticket>();

		if (!filterFormCriteria) {
			return tickets;
		}

		//consider rewriting this as a filter for the _readability_
		// Filter out empty filter criteria (early exit for null/ ignored stuff)
		const validFilterCriteria = Object.keys(filterFormCriteria).reduce((acc, key) => {
			if (
				(!!filterFormCriteria[key]?.operator &&
					filterFormCriteria[key].operator !== CommonPredicateOp.Ignore) ||
				(typeof filterFormCriteria[key] == 'string' && filterFormCriteria[key] !== '') ||
				(Array.isArray(filterFormCriteria[key]) && filterFormCriteria[key].length > 0)
			) {
				acc[key] = filterFormCriteria[key];
			}
			return acc;
		}, {} as Partial<FilterFormCriteria>);

		// Get the swimlane ids from the filter criteria (to avoid doing this in the loop)
		const swimlaneIds: string[] = validFilterCriteria[FormFields.SwimlaneIds];

		tickets.forEach((ticket) => {
			let passesCriteria = true;
			for (const controlName of Object.keys(validFilterCriteria)) {
				if (controlName === FormFields.SwimlaneIds) {
					// The swimlane filter is a special case because it is not a direct comparison
					// but a check to see if the ticket's top and bottom values falls within a swimlane's top and bottom values
					const swimlanesTree = this.get().swimlanesTree;
					const search = swimlanesTree.search({
						minX: 0,
						minY: ticket.view.top,
						maxX: 1,
						maxY: ticket.view.top,
					});

					// If search holds more than one swimlane, it means (most likely) that the ticket has the same top value as its swimlane
					// and at the same time it is the bottom of another swimlane (in cases where a swimlane's top is the same as another swimlane's bottom)
					// Rbush behavior: https://github.com/mourner/rbush/issues/108
					// Issue: https://mocasystems.atlassian.net/browse/TP-6516
					passesCriteria = search.some((swimlane) => {
						const swimlaneTop = swimlane.minY;
						return (
							swimlaneIds.includes(swimlane.swimlaneId) &&
							(search.length === 1 || ticket.view.top === swimlaneTop)
						);
					});
				} else {
					const { filterFieldCriteria, ticketValue } = this.castFieldsForComparison(
						controlName,
						ticket,
						filterFormCriteria
					);
					if (filterFieldCriteria.operator === CommonPredicateOp.Ignore) {
						continue;
					}

					passesCriteria = doesValueMatchCriteria(ticketValue, filterFieldCriteria);
				}
				if (
					(filterOperator === FilterOperator.OR && passesCriteria) ||
					(filterOperator === FilterOperator.AND && !passesCriteria)
				) {
					break;
				}
			}
			if (passesCriteria) {
				matchingTickets.set(ticket.$id, ticket);
			}
		});

		return matchingTickets;
	}

	/**
	 * There are some fields that need to be casted before comparing the ticket value with the filter value.
	 *
	 * @param controlName - The name of the control used for filtering.
	 * @param ticket - The ticket object used for filtering.
	 * @param filterFieldCriteria - The form field criteria used for filtering the tickets.
	 * @returns An object containing the casted ticket value and casted filter value.
	 */
	private castFieldsForComparison(
		controlName: string,
		ticket: Ticket,
		filterFormCriteria: FilterFormCriteria
	): { ticketValue: TicketValue; filterFieldCriteria: ITPFilter | ICustomFieldFilter } {
		const { ticketField, comparisonType } = criteriaMap.get(controlName);
		const ticketValue = ticket.view[ticketField];
		const controlValue = filterFormCriteria[controlName];
		let fieldResult: { ticketValue: TicketValue; filterFieldCriteria: ITPFilter };
		// if a ticket does not have type, it is a task
		if (ticketField === TicketFields.Type) {
			fieldResult = {
				ticketValue: ticketValue || TicketType.Task,
				filterFieldCriteria: {
					operator: controlValue.length == 0 ? CommonPredicateOp.AnyValue : comparisonType,
					value: controlValue,
				},
			};
		}

		// map ticket status to the corresponding string
		else if (ticketField === TicketFields.TicketStatus) {
			fieldResult = {
				ticketValue: TicketStatusMap.get(ticketValue) || ticketValue,
				filterFieldCriteria: {
					operator: controlValue.length == 0 ? CommonPredicateOp.AnyValue : comparisonType,
					value: controlValue,
				},
			};
		} else if (
			controlName == FormFields.CustomField1 ||
			controlName == FormFields.CustomField2 ||
			controlName == FormFields.CustomField3
		) {
			// If controlValue operator is AnyValue,
			// then the filter should return tickets that have any value for a specific custom field
			// So the ticket value should be an array of its custom field ids
			// and the filterFieldCriteria should be set to Contains the custom field id selected in the filter
			if (controlValue.operator === CommonPredicateOp.AnyValue) {
				fieldResult = {
					ticketValue: this.get().ticketCustomFields?.[ticket.$id]?.map(
						(customField) => customField.customFieldId
					),
					filterFieldCriteria: {
						operator: TextPredicateOp.Contains,
						value: controlValue.customFieldId,
					},
				};
			}
			// If controlValue operator is not AnyValue,
			// then it means it should compare by an specific value
			else {
				fieldResult = {
					ticketValue: this.get().ticketCustomFields?.[ticket.$id]?.map(
						(customField) => customField.value
					) as string[],
					filterFieldCriteria: {
						operator: controlValue.operator,
						value: controlValue.value,
					},
				};
			}
		} else if (controlName == FormFields.TicketAttachments) {
			/**
			 * Any value: AnyValue
			 * With attachments: GreaterThan 0 attachments
			 * Without attachments: EqualTo 0 attachments
			 */
			const comparisonValue = 0;
			const mapOperator = {
				[TicketAttachmentOptions.Any]: CommonPredicateOp.Ignore,
				[TicketAttachmentOptions.WithAttachments]: NumericPredicateOp.GreaterThan,
				[TicketAttachmentOptions.WithoutAttachments]: NumericPredicateOp.EqualTo,
			};

			fieldResult = {
				ticketValue: this.get().ticketAttachments?.[ticket.$id]?.length || 0,
				filterFieldCriteria: {
					operator: mapOperator[controlValue],
					value: comparisonValue,
				},
			};
		}

		// form field that already is a ITPFilter
		else if (controlValue?.operator) {
			fieldResult = {
				ticketValue,
				filterFieldCriteria: {
					...controlValue,
					operator: controlValue.operator,
				},
			};
		} else {
			fieldResult = {
				ticketValue,
				filterFieldCriteria: {
					operator: comparisonType,
					value: controlValue,
				},
			};
		}

		// Cast the ticket value and filter value to lowercase for case-insensitive comparison
		if (
			controlName == FormFields.WbsCode ||
			controlName == FormFields.Description ||
			controlName == FormFields.ExternalScheduleId
		) {
			fieldResult.ticketValue = fieldResult.ticketValue?.toString().trim().toLowerCase();
			fieldResult.filterFieldCriteria.value = fieldResult.filterFieldCriteria.value
				?.toString()
				.trim()
				.toLowerCase();
		}

		if (controlName == FormFields.AttachmentComment) {
			fieldResult.ticketValue = this.get().ticketAttachments?.[ticket.$id]?.map((attachment) =>
				attachment.description?.toString().trim().toLowerCase()
			);
			fieldResult.filterFieldCriteria.value = fieldResult.filterFieldCriteria.value
				?.toString()
				.trim()
				.toLowerCase();
		}

		if (controlName === FormFields.RoleIds && ticket.view.type === TicketType.Constraint) {
			fieldResult.ticketValue = [
				ticket.view.requestedByProjectMemberRoleId,
				ticket.view.responsibleProjectMemberRoleId,
				ticket.view.assignedProjectMemberRoleId,
			];
			fieldResult.filterFieldCriteria.value = controlValue;
		}

		return fieldResult;
	}

	private getPlanFilteringUserPreferenceRef() {
		const { userId, projectId } = this.get();
		if (userId && projectId) {
			const planFilteringPath = fancyFirebaseGetters.getProjectUserPreferencesPath(
				projectId,
				userId,
				UserPreferenceCategories.PlanFilteringTicketPrefs
			);
			return fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(planFilteringPath, projectId);
		}
		return null;
	}

	/**
	 * Processes the ticket attachments data to be used in the filter.
	 * @param data ITicketAttachmentList
	 * @returns
	 */
	private processTicketAttachmentsForFilter(data: ITicketAttachmentList | undefined) {
		if (!data) {
			return {} as IPlanTicketsAttachments;
		}
		return Object.entries(data).reduce((acc, current) => {
			const [ticketId, attachments] = current;
			acc[ticketId] = Object.values(attachments);
			return acc;
		}, {} as IPlanTicketsAttachments);
	}

	ngrxOnStoreInit(): void {
		/*
		/ This effect listens to changes in the user id and project id
		/ and fetches the filter criteria from the database
		/ and sets the filter form criteria
		*/
		this.effect(() =>
			combineLatest([this.userId$, this.projectId$]).pipe(
				filter(([userId, projectId]) => !!userId && !!projectId),
				switchMap(() => {
					const ref = this.getPlanFilteringUserPreferenceRef();
					if (!!ref) {
						return this.fireDB.object<IFirebaseCriteriaStore>(ref).valueChanges().pipe(take(1));
					}
					this.logger.error('Error fetching filter criteria values, reference is null');
					return EMPTY;
				}),
				tapResponse(
					(filterFormCriteria: IFirebaseCriteriaStore) => {
						if (filterFormCriteria) {
							const { filterOperator, ...restOfFilters } = filterFormCriteria;
							this.setFilterFormCriteria(restOfFilters);
							this.setSelectedFilterOperator(filterOperator);
						}
						// Set the server data loaded flag to true after the filter criteria are set
						// to ensure that the filter criteria are set only once.
						// and avoid a small risk that the loaded event could fire before filterFormCriteria$
						this.setServerDataLoaded(true);
					},
					(error) => {
						this.logger.error('Error listening to filter criteria changes', error);
					}
				)
			)
		);

		// This effect listens to changes in the swimlanes and creates an RBush tree for the swimlanes
		this.effect(() =>
			this.swimlaneOptions$.pipe(
				filter((swimlanes) => !!swimlanes && swimlanes.size > 0),
				tapResponse(
					(swimlanes: Map<string, Swimlane>) => {
						const swimlanesTree = new RBush(swimlanes.size);
						const items = Array.from(swimlanes.values()).map((swimlane) => ({
							minX: 0,
							minY: swimlane.view.top,
							maxX: 1,
							maxY: swimlane.view.bottom,
							swimlaneId: swimlane.$id,
						}));
						swimlanesTree.load(items);
						this.setSwimlanesTree(swimlanesTree);
					},
					(error) => {
						this.logger.error('Error creating swimlanes RBush tree', error);
					}
				)
			)
		);
	}

	private storeFilterCriteriaValues(filterCriteriaData: IFirebaseCriteriaStore) {
		const ref = this.getPlanFilteringUserPreferenceRef();
		if (ref) {
			this.fireDB.object<IFirebaseCriteriaStore>(ref).set(filterCriteriaData);
		} else {
			this.logger.error('Error storing filter criteria values, reference is null');
		}
	}

	override ngOnDestroy(): void {
		this.resetFilteringProperties();
		super.ngOnDestroy();
	}

	constructor() {
		super(INITIAL_STATE, INITIAL_OBS_STATE);
	}
}
